import React, { useMemo, useState } from 'react';

import FileAPI from 'services/api/file.api';
import FileDropZone from 'components/Shared/FileDropZone/FileDropZone';
import { showSnackbar } from 'store/snackbar/snackbar.action';
import { useAppSelector } from 'hooks/reduxHooks';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

type TFileUploadProps = {
  allowedTypes?: string[];
  showDownloadSampleFile?: boolean;
};

const CSV_UPLOAD_FILE_LIMIT = 200099999;

const FileUpload: React.FC<TFileUploadProps> = ({ allowedTypes, showDownloadSampleFile = false }) => {
  const fileApi = useMemo(() => new FileAPI(), []);

  const [isBusy, setIsBusy] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  const [fileUploadedPercentage, setFileUploadedPercentage] = useState(0);
  const filesUUID = useAppSelector((state) => state.te.journey.metadata?.filesUUID || '');
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const uploadFiles = async (files: FileList) => {
    setFilesUploaded(1);
    setTotalFiles(files.length);
    setIsBusy(true);

    const filePromises = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > CSV_UPLOAD_FILE_LIMIT) {
        dispatch(
          showSnackbar({
            content: `The file "${files[i].name}" is too large. The maximum file size is 200MB.`,
            type: 'error',
          }),
        );
      } else if (file.type !== 'text/csv') {
        dispatch(
          showSnackbar({
            content: `The file "${files[i].name}" is not a valid CSV file.`,
            type: 'error',
          }),
        );
      } else {
        const promise = fileApi.uploadViaPreSignedUrl(files[i], setFileUploadedPercentage, filesUUID);

        promise.then(() => {
          if (filesUploaded < totalFiles) {
            setFilesUploaded((oldState) => oldState + 1);
          }
        });

        filePromises.push(promise);
      }
    }

    if (filePromises.length) {
      const rejected = (await Promise.allSettled(filePromises)).filter((r) => r.status === 'rejected');

      if (rejected.length) {
        dispatch(
          showSnackbar({
            content: `${
              totalFiles > 1 ? 'Some of your files did not upload successfully.' : 'The file failed to upload.'
            } ${rejected[0].reason.message}`,
            type: 'error',
          }),
        );
      } else {
        const message = `${
          totalFiles > 1 ? 'All files have been successfully uploaded.' : 'The file has been uploaded successfully.'
        }`;

        dispatch(
          showSnackbar({
            content: message,
            type: 'success',
          }),
        );
      }
    }

    await queryClient.invalidateQueries(['ctx-data-file']);
    setIsBusy(false);
  };

  return (
    <FileDropZone
      allowedTypes={allowedTypes}
      showDownloadSampleFile={showDownloadSampleFile}
      onFilesSelected={uploadFiles}
      isBusy={isBusy}
      totalFiles={totalFiles}
      filesUploaded={filesUploaded}
      fileUploadedPercentage={fileUploadedPercentage}
    />
  );
};

export default FileUpload;
